import type { Event } from '@sentry/react'
import { localStore } from 'utils/storage'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

interface CfEmployeeState {
  isCfEmployee: boolean
}

const initialState: CfEmployeeState = { isCfEmployee: false }

/**
 * Persisted so a page reload starts with the boosted sample rate immediately, instead of sampling
 * at the base rate until the user query resolves. `configureSampleRate` reads it synchronously
 * during Sentry init; zustand/persist hydrates from localStorage synchronously on module load.
 */
export const useCfEmployeeStore = create<CfEmployeeState>()(
  persist(() => initialState, {
    name: 'sentry:cf-employee',
    storage: {
      getItem: localStore.getJSON,
      setItem: localStore.setJSON,
      removeItem: localStore.remove,
    },
  }),
)

let baseSampleRate = 1
let effectiveSampleRate = 1

/**
 * Called by the Sentry init helpers with the app's configured error `sampleRate`. The SDK option
 * itself is set to 1 and sampling is applied by `sampleRateEventProcessor` instead, because the
 * SDK offers no supported way to change `sampleRate` after `Sentry.init`.
 */
export function configureSampleRate(configuredSampleRate: number | undefined) {
  baseSampleRate = configuredSampleRate ?? 1
  effectiveSampleRate = useCfEmployeeStore.getState().isCfEmployee ? 1 : baseSampleRate
}

export function setSampleRate(sampleRate: number) {
  effectiveSampleRate = Math.min(Math.max(sampleRate, 0), 1)
}

export function resetSampleRate() {
  effectiveSampleRate = baseSampleRate
}

/**
 * Persists whether the current user is a CF employee and boosts the error sample rate to 100% for
 * employees (or restores the configured base rate otherwise), so employee sessions never lose
 * error events to sampling.
 */
export function setCfEmployeeSampleRate(isCfEmployee: boolean) {
  useCfEmployeeStore.setState({ isCfEmployee })

  if (isCfEmployee) {
    setSampleRate(1)
  } else {
    resetSampleRate()
  }
}

/**
 * Runtime replacement for the SDK's static `sampleRate` option. Mirrors the SDK's own behavior:
 * only error events (no `type`) are sampled, everything else passes through untouched.
 *
 * `random` is injectable so tests can pin the sampling decision.
 */
export function createSampleRateEventProcessor(random: () => number = Math.random) {
  return function sampleRateEventProcessor(event: Event): Event | null {
    if (event.type !== undefined) {
      return event
    }

    return random() > effectiveSampleRate ? null : event
  }
}

export const sampleRateEventProcessor = createSampleRateEventProcessor()
